import PaymentMethodState from "./method";
import Currency from "../../amount/currency";
import { $processing } from "../payment/helpers/charge";
import { $successful, PAYMENT_METHODS } from "../payment";
import { combineReducers, bindActionCreators } from "redux";
import { withProperties, getValueAtPath, isPlainObject } from "../../utils";
import withSwitchStateFactory from "../helpers/reducers/switch";
import withConstantStateFactory from "../helpers/reducers/constant";
import { PREFERRED_PAYMENT_OPTIONS_SEQUENCE } from "../../init/initdata";
import { getPreferredPaymentOption } from "../../init/paymentoptions";

import {
  START_SWITCH_CURRENCY,
  STOP_SWITCH_CURRENCY,
  SWITCH_CURRENCY,
  UPDATE_PAYMENT_METHODS
} from "../helpers/actions";

const OPTIONS_SEQUENCE = Object.fromEntries(
  Array.from(PREFERRED_PAYMENT_OPTIONS_SEQUENCE, (v, i) => [v, i])
);

export default function state() {
  const NAME = 'currency';
  const $this = this;
  const $state = Object.create(null);
  const { data, dispatch } = this;

  const availableCurrencies = getValueAtPath(data, 'currencies');
  const initialCurrency = getValueAtPath(data, 'initial_currency');
  const enabledForDCC = getValueAtPath(data, 'preferences[use_dynamic_currency_conversion]');
  const hasIntegrityChecksum = getValueAtPath(data, 'integrity_hash');
  
  const canSwitchCurrencies = enabledForDCC && !hasIntegrityChecksum;
  const initialPaymentMethods = __getAvailablePaymentMethods__.call($this, initialCurrency);

  const ConstantState = withConstantStateFactory({
    available: canSwitchCurrencies ? availableCurrencies : Object.freeze([ initialCurrency ].filter(Boolean))
  });

  const $switching = withSwitchStateFactory.call(this, {
    actions: { on: START_SWITCH_CURRENCY, off: STOP_SWITCH_CURRENCY },
    initialState: false,
    key: 'switching'
  });

  const Reducer = combineReducers({
    ...$switching,
    ...ConstantState,

    current(state = initialCurrency, action) {
      switch (action.type) {
        case SWITCH_CURRENCY: {
          const { currency } = payload;
          return (availableCurrencies.indexOf(currency) >= 0 && currency !== state) ? currency : state;
        }

        default: return state;
      }
    },

    methods(state = initialPaymentMethods, action) {
      switch (action.type) {
        case UPDATE_PAYMENT_METHODS: {
          const { current } = $state.STATE;
          return __getAvailablePaymentMethods__.call($this, current);
        }

        default: return state;
      }
    }
  });

  // Augment $state with properties
  withProperties($state, {
    NAME,
    Reducer,
    actions: withProperties(Object.create(null), { switchCurrency }),
    exposedActions: withProperties(Object.create(null), {
      Currency: bindActionCreators({ switch: switchCurrency }, dispatch)
    })
  });

  function __getAvailablePaymentMethods__(currency) {
    const methods = (isPlainObject(this) && getValueAtPath(this.data, `currency_options[${currency}]`) || [])
      .sort((a, b) => (OPTIONS_SEQUENCE[a] - OPTIONS_SEQUENCE[b]) || 0);

    const labelsMap = methods.reduce((map, method) => {
      return withProperties(map, {
        [method]: getValueAtPath(PAYMENT_METHODS, `${method}[name]`) || null
      });
    }, Object.create(null));

    return Object.freeze(Object.defineProperties(methods, {
      labels: { value: Object.freeze(labelsMap) }
    }));
  }

  function __activateFirstPaymentMethod__() {
    return (dispatch) => {
      const { methods, current: currentCurrency } = $state.STATE;
      const { current: currentMethod } = PaymentMethodState.STATE;

      // Resolve first payment method to display
      const method = methods.includes(currentMethod)
        ? currentMethod
        : getPreferredPaymentOption(currentCurrency, methods) ||
          (currentCurrency === 'NGN' && methods.indexOf('card') >= 0
            ? 'card'
            : methods[0]);

      dispatch(PaymentMethodState.actions.switchMethod(method));
    }
  }

  function switchCurrency(currency) {
    return (dispatch) => {
      if ($successful !== true && $processing !== true && canSwitchCurrencies) {
        const { current } = $state.STATE;
        currency = String(Currency.from(currency));

        if (!(currency === Currency.NONE || currency === current) && Currency.DCC.indexOf(currency) >= 0) {
          dispatch($switching.actions.on());

          // Trigger currency conversion service
          // (using a dummy promise here)
          Promise.resolve()
            .then(() => {
              dispatch({ type: SWITCH_CURRENCY, payload: { currency } });
              dispatch({ type: UPDATE_PAYMENT_METHODS });
              dispatch(__activateFirstPaymentMethod__());
              dispatch($switching.actions.off());
            });
        }
      }
    }
  }

  // Initially, $state will be an empty object, because the setup function
  // has not been called. Using setTimeout() here ensures that the dispatch
  // action happens on the next tick of the event loop.
  setTimeout(() => {
    dispatch(__activateFirstPaymentMethod__());
  }, 0);

  return (state = $state);
}
